import { useClerk, useSignIn } from '@clerk/clerk-react';
import { EmailLinkErrorCodeStatus, isEmailLinkError } from '@clerk/clerk-react/errors';
import { EmailLinkFactor, SignInFirstFactor } from '@clerk/types';
import { CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router';

import { Button } from '~components/commons/buttons/Button';
import AuthLayout from '~components/login/AuthLayout/AuthLayout';

import { Logo, RedDismissIcon } from '~assets/images';
import { ClerkError } from '~types/clerk';

import styles from './EmailLinkVerification.module.scss';

enum VerificationStatus {
	LOADING = 'loading',
	VERIFIED = 'verified',
	FAILED = 'failed',
	EXPIRED = 'expired',
	CLIENT_MISMATCH = 'client_mismatch',
}

const errorMessagesDictionary = {
	[VerificationStatus.FAILED]: 'We were not able to verify your account. ',
	[VerificationStatus.EXPIRED]: 'The link expired. ',
	[VerificationStatus.CLIENT_MISMATCH]:
		'Please complete the login on the same device and browser you started from.',
};

export const EmailLinkVerification: React.FC = () => {
	const { handleEmailLinkVerification, loaded: isLoaded } = useClerk();
	const { signIn, isLoaded: isSignInLoaded, setActive } = useSignIn();

	const [verificationStatus, setVerificationStatus] =
		React.useState<VerificationStatus>(VerificationStatus.LOADING);

	const emailAddress = decodeURIComponent(
		new URLSearchParams(window.location.search).get('emailAddress') || '',
	);

	const verifyEmailLink = useCallback(async () => {
		try {
			// Dynamically set the host domain for dev and prod
			// You could instead use an environment variable or other source for the host domain
			const { protocol } = window.location;
			const { host } = window.location;

			await handleEmailLinkVerification({
				// URL to navigate to if sign-in flow needs more requirements, such as MFA
				redirectUrlComplete: `${protocol}//${host}/?clerkAuthentication=true`,
			});

			// If not redirected at this point,
			// the flow has completed
			setVerificationStatus(VerificationStatus.VERIFIED);
		} catch (error) {
			let status = VerificationStatus.FAILED;
			if (isEmailLinkError(error)) {
				// If link expired, set status to expired
				if (error.code === EmailLinkErrorCodeStatus.Expired) {
					status = VerificationStatus.EXPIRED;
				} else if (error.code === EmailLinkErrorCodeStatus.ClientMismatch) {
					// OPTIONAL: This check is only required if you have
					// the 'Require the same device and browser' setting
					// enabled in the Clerk Dashboard
					status = VerificationStatus.CLIENT_MISMATCH;
				}
			}

			setVerificationStatus(status);
			return;
		}
	}, [handleEmailLinkVerification]);

	const handleSendAnotherLink = useCallback(async () => {
		if (!isSignInLoaded || !emailAddress) {
			toast.error('Invalid email address.', {
				id: 'clerk-not-loaded',
			});
			return;
		}

		try {
			const { supportedFirstFactors } = await signIn.create({
				identifier: emailAddress,
			});

			const emailLinkFactor = supportedFirstFactors?.find(
				(factor: SignInFirstFactor): factor is EmailLinkFactor =>
					factor.strategy === 'email_link',
			);

			if (!emailLinkFactor) {
				toast.error('Clerk did not find an email link factor.', {
					id: 'clerk-email-link-factor-not-found',
				});
				return;
			}

			const { emailAddressId } = emailLinkFactor;

			const { protocol } = window.location;
			const { host } = window.location;

			const { startEmailLinkFlow } = signIn.createEmailLinkFlow();

			toast.success('Trying to send another link...');
			const signInAttempt = await startEmailLinkFlow({
				emailAddressId,
				redirectUrl: `${protocol}//${host}/email-link/verify?emailAddress=${encodeURIComponent(
					emailAddress,
				)}`,
			});

			const verification = signInAttempt.firstFactorVerification;

			if (verification.status === 'expired') {
				toast.error('The link expired.', {
					id: 'clerk-email-link-expired',
				});
				return;
			}

			if (verification.verifiedFromTheSameClient()) {
				setActive({ session: signInAttempt.createdSessionId });
			}
		} catch (error) {
			Sentry.captureException(error);
			toast.error(
				(error as ClerkError).errors[0].longMessage || 'Something went wrong',
			);
		}
	}, [emailAddress, isSignInLoaded, signIn, setActive]);

	useEffect(() => {
		if (isLoaded) {
			verifyEmailLink();
		}
	}, [isLoaded, verifyEmailLink]);

	return (
		<AuthLayout>
			<div className={styles.container}>
				{verificationStatus === VerificationStatus.LOADING && (
					<div className={styles.progressContainer}>
						<Logo className={styles.absoluteLogo} />
						<CircularProgress className={styles.progress} size={72} />
						<h4>Authenticating...</h4>
					</div>
				)}

				{verificationStatus !== VerificationStatus.LOADING && (
					<React.Fragment>
						<Logo className={styles.logo} />
						<div className={styles.cardContainer}>
							{verificationStatus === VerificationStatus.VERIFIED && (
								<React.Fragment>
									<h2>Success!</h2>
									<p>Successfully signed in.</p>
								</React.Fragment>
							)}

							{verificationStatus !== VerificationStatus.VERIFIED && (
								<React.Fragment>
									<RedDismissIcon className={styles.dismissIcon} />
									<h2>Let&apos;s try again</h2>
									<p>{errorMessagesDictionary[verificationStatus]}</p>
									<p>
										Click the button below and we&apos;ll send a new
										link{' '}
										{emailAddress && (
											<>
												<span>to </span>
												<span className={styles.bold}>
													{emailAddress}
												</span>
											</>
										)}
									</p>
									<div className={styles.actionButtonsContainer}>
										<Button
											onClick={handleSendAnotherLink}
											disabled={!isSignInLoaded}
										>
											Send another email link
										</Button>
										<Link to="/login">Switch account</Link>
									</div>
								</React.Fragment>
							)}
						</div>
					</React.Fragment>
				)}
			</div>
		</AuthLayout>
	);
};
